import React, {ReactElement} from "react";
import {navigate} from "gatsby";
import {useStaticQuery, graphql} from "gatsby";

import SimpleFormWrapper from "../../forms/simple-form-wrapper/SimpleFormWrapper";

import {logGoogleAnalyticsEvent} from "/src/utils/google-analytics";
import {logEvent, updateUserProperties} from "/src/utils/amplitude";
import {logErrorToSentry} from "/src/utils/sentry";
import {LOCALE} from "/src/config";
import {MaybeType, SimpleFormProps} from "/src/types";

import * as styles from "./lead-form.module.scss";
import {css} from "/src/utils/utils";

export interface FormData {
  // eslint-disable-next-line camelcase
  first_name: string;
  // eslint-disable-next-line camelcase
  last_name: string;
  email: string;
  phone: string;
  company: string;
  // eslint-disable-next-line camelcase
  job_title: string;
  country: string;
  // eslint-disable-next-line camelcase
  Annual_Shipments_Signup: string;
  privacyPolicyAccepted: boolean;
  expressOptIn: boolean;
}

interface LeadFormProps {
  buttonText: string;
  campaignId: string;
  classes?: string;
  confirmationRoute?: MaybeType<string>;
  headline: string;
  lastInquiryObject: string;
  onMount?: () => void;
  onSubmitError?: (e: Error) => unknown;
  onSubmitSuccess?: (data: FormData) => unknown;
  pardotFormHandlerUrl?: MaybeType<string>;
}

const DEFAULT_SUCCESS_PAGE = "/thank-you/confirmation-page/";

const LeadForm = (props: LeadFormProps): ReactElement => {
  const {
    buttonText,
    campaignId,
    classes = "",
    confirmationRoute,
    headline,
    lastInquiryObject,
    onMount,
    onSubmitError,
    onSubmitSuccess,
    pardotFormHandlerUrl,
  } = props;

  const data = useStaticQuery(
    graphql`
      query LeadFormQuery {
        allContentfulSimpleForm(
          filter: {
            contentful_id: {eq: "3iVlpMbwCx7JAqnW58wsny"}
            node_locale: {in: ["en-US", "zh-CN"]}
          }
        ) {
          nodes {
            node_locale
            ...SimpleFormFragment
          }
        }
      }
    `
  );

  const localizedForm: SimpleFormProps =
    data.allContentfulSimpleForm.nodes.find(
      (el: Record<string, string>) => el.node_locale === LOCALE
    );

  const defaultSubmit = () => {
    navigate(DEFAULT_SUCCESS_PAGE);
  };

  const onSubmitSuccessDefault = onSubmitSuccess || defaultSubmit;

  const handleSubmit = (data: FormData, error?: Error): boolean => {
    if (error) {
      logErrorToSentry(error);
      if (onSubmitError != null) {
        onSubmitError(error);
      }
      return false;
    }

    try {
      logEvent("click:marketing_site_cta", {
        name: "lead_form",
        type: "sign_up",
      });

      updateUserProperties({email: data.email});

      logGoogleAnalyticsEvent("send", "event", "Sign Up", "Lead Form Submit");
      onSubmitSuccessDefault(data);

      return true;
    } catch (err: any) {
      logErrorToSentry(err);
      if (onSubmitError != null) {
        onSubmitError(err);
      }

      return false;
    }
  };

  return (
    <SimpleFormWrapper
      {...localizedForm}
      campaignId={campaignId}
      classes={css(styles.leadForm__form, classes)}
      contentClasses={styles.leadForm__form__content}
      lastInquiryObject={lastInquiryObject}
      onMount={onMount}
      onSubmitCallback={handleSubmit}
      pardotFormHandlerUrl={pardotFormHandlerUrl!}
      postSubmissionUrl={confirmationRoute!}
      submitButtonLabel={buttonText}
      title={headline}
    />
  );
};

export default LeadForm;
